import { Component } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { tap, first } from 'rxjs/operators';
import { indicate } from 'src/app/operators';
import { DictionaryItem, DictionaryItemDefinition, DictionarySimplePageComponent } from 'src/app/shared/components/dictionary-simple-page/dictionary-simple-page.component';
import { EsraPipingAPIClient, PipeClassSummaryPropertyDictionaryViewModel } from 'src/app/shared/models/autogenerated-piping';
import { InsightsService } from 'src/app/shared/services/insights.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { DictionaryExportService } from '../services/dictionary-export.service';
import { UserService } from 'src/app/core/services/user.service';
import { Constants } from 'src/app/shared/constants/constants';

/** The 7 property names backed by the PipeClassSummaryPropertyDictionary table. */
export const SUMMARY_PROPERTY_NAMES: { label: string; value: string }[] = [
  { label: 'All',                         value: '' },
  { label: 'Stress Relief',               value: 'stressRelief' },
  { label: 'Nominal Corrosion Allowance', value: 'nomCorAllow' },
  { label: 'Rating Class',                value: 'ratingClass' },
  { label: 'Examination',                 value: 'examination' },
  { label: 'Limited By',                  value: 'limitedBy' },
  { label: 'Material',                    value: 'material' },
  { label: 'Valve Trim',                  value: 'valveTrim' },
];

@Component({
  selector: 'app-pipe-class-summary-properties',
  templateUrl: './pipe-class-summary-properties.component.html',
  styleUrls: [
    '../../../../shared/components/dictionary-simple-page/dictionary-simple-page.component.scss',
    './pipe-class-summary-properties.component.scss',
  ],
  standalone: false,
})
export class PipeClassSummaryPropertiesComponent extends DictionarySimplePageComponent<PipeClassSummaryPropertyDictionaryViewModel> {
  /** The fixed list of property-name filter options shown in the dropdown. */
  readonly propertyNameOptions: { label: string; value: string }[] = SUMMARY_PROPERTY_NAMES;

  /** Currently selected property name filter ('', 'stressRelief', …). */
  selectedPropertyName: string = '';

  /** True when the current user has the dictionary-write role and may add values. */
  get canAddValue(): boolean {
    return this.userService.isRoleExists(Constants.pipingDictionaryWriter)
        || this.userService.isRoleExists(Constants.admin);
  }

  // ── Add-value inline form ────────────────────────────────────────────────
  isAddFormOpen: boolean = false;
  /** Property name selected in the add form; defaults to the active filter. */
  addPropertyName: string = '';
  addValue: string = '';
  addErrorMessage: string | null = null;
  saving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /** Options for the add-form property dropdown (excludes the 'All' entry). */
  readonly addPropertyNameOptions: { label: string; value: string }[] =
    SUMMARY_PROPERTY_NAMES.filter(o => o.value !== '');

  constructor(
    private readonly esraApiClient: EsraPipingAPIClient,
    private readonly snackBarService: SnackBarService,
    private readonly insightsService: InsightsService,
    private readonly dictionaryExportService: DictionaryExportService,
    private readonly userService: UserService
  ) {
    super();
    // Wire the export to pass the active property-name filter so the Excel file
    // only contains the rows the user is currently viewing.
    this.exportFn = () => this.dictionaryExportService.exportPipeClassSummaryProperties(
      this.selectedPropertyName || undefined
    );
  }

  protected childOnInitBefore(): void { /* not needed */ }
  protected childOnInitAfter(): void { /* not needed */ }

  /** Called by the template when the property-name dropdown changes. */
  onPropertyNameChange(value: string): void {
    this.selectedPropertyName = value;
    this.searchAll();
  }

  protected searchAll(): void {
    this.esraApiClient
      .getAllPipeClassSummaryPropertyDictionaryPaged(
        this.selectedPropertyName || undefined,
        undefined,
        undefined,
        undefined,
        true // showAll — client-side filter handles active/inactive toggle
      )
      .pipe(
        indicate(this.searching$),
        first(),
        tap((items: PipeClassSummaryPropertyDictionaryViewModel[]) => {
          this.totalCount = items.length;
          this.dataSource.data = items;
        })
      )
      .subscribe();
  }

  protected initializeDictionaryDefinition(): DictionaryItemDefinition<PipeClassSummaryPropertyDictionaryViewModel> {
    return {
      displayStatusColumn: true,
      dictionaryItems: [
        new DictionaryItem('Last Updated',   'updated',      'standard', (item) => this.getDate(item)),
        new DictionaryItem('Property Name',  'propertyName', 'standard'),
        new DictionaryItem('Value',          'value',        'extended'),
      ],
    };
  }

  private getDate(item: PipeClassSummaryPropertyDictionaryViewModel): string {
    if (!item.updated) { return ''; }
    return String(item.updated.getMonth() + 1).padStart(2, '0') + '/' +
           String(item.updated.getDate()).padStart(2, '0') + '/' +
           item.updated.getFullYear();
  }

  protected getFilterPredicate(
    record: PipeClassSummaryPropertyDictionaryViewModel,
    parametersMap: Map<string, string>
  ): boolean {
    const searchQuery: string = parametersMap.get(DictionarySimplePageComponent.SEARCH_QUERY_NAME)?.toLowerCase() ?? '';
    const isActiveFilter: string = parametersMap.get(DictionarySimplePageComponent.IS_ACTIVE_NAME);

    const matchesSearch: boolean =
      (record.value?.toLowerCase().includes(searchQuery) ?? false) ||
      (record.propertyName?.toLowerCase().includes(searchQuery) ?? false) ||
      (record.updated ? this.getDate(record).toLowerCase().includes(searchQuery) : false);

    if (isActiveFilter == null) {
      return matchesSearch;
    }
    return matchesSearch && record.isActive === JSON.parse(isActiveFilter);
  }

  protected getNoMatchMessage(): string {
    return 'No pipe class summary properties match the provided search criteria';
  }

  /** No "create new" button needed — creation is handled by the + Add Value form. */
  protected createNew(): void { /* handled by openAddForm() */ }

  /** Opens the inline add form, pre-selecting the current property filter if set. */
  openAddForm(): void {
    this.addPropertyName = this.selectedPropertyName || this.addPropertyNameOptions[0]?.value || '';
    this.addValue = '';
    this.addErrorMessage = null;
    this.isAddFormOpen = true;
  }

  /** Closes the add form without saving. */
  cancelAdd(): void {
    this.isAddFormOpen = false;
    this.addErrorMessage = null;
  }

  /**
   * Calls POST Create and reloads the table on success.
   * 400 responses (duplicate value) are shown inline in the form banner.
   */
  submitAdd(): void {
    if (!this.addPropertyName || !this.addValue.trim()) { return; }
    this.addErrorMessage = null;

    this.esraApiClient
      .createPipeClassSummaryPropertyDictionary(this.addPropertyName, this.addValue.trim())
      .pipe(indicate(this.saving$), first())
      .subscribe({
        next: () => {
          this.snackBarService.showSnackBar(
            true,
            `"${this.addValue.trim()}" was added to ${this.addPropertyName}.`,
            'mat-snack-bar-info'
          );
          this.isAddFormOpen = false;
          this.searchAll();
        },
        error: (err) => {
          // NSwag throws the raw string for 400 responses — same pattern as wizard error handling.
          const message: string = typeof err === 'string'
            ? err
            : (typeof err?.response === 'string' ? err.response : null)
              ?? 'Failed to add value. Please try again.';
          this.addErrorMessage = message;
        },
      });
  }

  protected editItem(_item: PipeClassSummaryPropertyDictionaryViewModel): void { /* read-only rows */ }

  protected editItemReferenced(_item: PipeClassSummaryPropertyDictionaryViewModel): void { /* read-only */ }

  protected deleteItem(item: PipeClassSummaryPropertyDictionaryViewModel): void {
    this.esraApiClient.togglePipeClassSummaryPropertyDictionaryStatus(item.id).subscribe({
      next: (updated: PipeClassSummaryPropertyDictionaryViewModel) => {
        const index: number = this.dataSource.data.findIndex(r => r.id === item.id);
        if (index !== -1) {
          this.dataSource.data[index] = updated;
          this.dataSource.data = [...this.dataSource.data]; // trigger change detection
        }
        const newState: string = item.isActive ? 'deactivated' : 'activated';
        this.snackBarService.showSnackBar(
          true,
          `"${item.value}" was successfully ${newState}.`,
          'mat-snack-bar-info'
        );
        this.search();
      },
      error: (error) => {
        this.snackBarService.showSnackBar(
          true,
          `Sorry, something went wrong toggling "${item.value}".`,
          'mat-snack-bar-error'
        );
        this.insightsService.logException(error);
      },
    });
  }

  protected updateStatus(item: PipeClassSummaryPropertyDictionaryViewModel): void {
    this.deleteItem(item);
  }

  protected getUsageInformation(_item: object): void { /* not applicable */ }
}
